import { useMemo } from "react";
import { AlertTriangle, BrainCircuit, CheckCircle2, ClipboardList, Database, LineChart, Pill, Zap } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AdvancedCurveExplorer } from "@/components/clinical/AdvancedCurveExplorer";
import { DoseLogManager } from "@/components/clinical/DoseLogManager";
import { buildClinicalIntelligenceSummary, type ClinicalSeverity } from "@/domain/clinicalIntelligenceEngine";
import { cn } from "@/lib/utils";

function toneClass(severity?: ClinicalSeverity) {
  switch (severity) {
    case "urgente": return "border-destructive/50 bg-destructive/5 text-destructive";
    case "relevante": return "border-warning/60 bg-warning/5 text-warning";
    case "cautela": return "border-warning/40 bg-warning/5 text-foreground";
    case "monitorar": return "border-info/40 bg-info/5 text-foreground";
    default: return "border-border bg-card text-foreground";
  }
}

function pct(v: number | null | undefined) {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${Math.round(v)}%`;
}

function num(v: number | null | undefined) {
  if (v == null || !Number.isFinite(v)) return "—";
  return String(Math.round(v));
}

export function IntegratedClinicalConsole({
  patient,
  medications = [],
  doses = [],
  checkins = [],
  sessions = [],
  substanceUse = [],
  inventory = [],
  periodDays = 30,
  targetSymptoms = [],
  symptomMeasurements = [],
  onDoseChanged,
  sessionId,
}: {
  patient?: any;
  medications?: any[];
  doses?: any[];
  checkins?: any[];
  sessions?: any[];
  substanceUse?: any[];
  inventory?: any[];
  periodDays?: number;
  targetSymptoms?: any[];
  symptomMeasurements?: any[];
  onDoseChanged?: () => void;
  sessionId?: string | null;
}) {
  const summary = useMemo(() => buildClinicalIntelligenceSummary({ patient, medications, doses, checkins, sessions, substanceUse, inventory, periodDays, targetSymptoms, symptomMeasurements }), [patient, medications, doses, checkins, sessions, substanceUse, inventory, periodDays, targetSymptoms, symptomMeasurements]);

  const patientId = patient?.id ?? doses?.[0]?.patient_id ?? medications?.[0]?.patient_id;

  return (
    <div className="min-w-0 space-y-4">
      <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Metric icon={<BrainCircuit className="h-4 w-4" />} label="Risco composto" value={`${summary.riskScore}/100`} sub={summary.riskLabel} severity={summary.riskLabel} />
        <Metric icon={<CheckCircle2 className="h-4 w-4" />} label="Qualidade dados" value={`${summary.dataQualityScore}/100`} sub={summary.missingCore.slice(0, 1).join("; ") || "núcleo ok"} severity={summary.dataQualityScore < 70 ? "cautela" : "info"} />
        <Metric icon={<Pill className="h-4 w-4" />} label="Doses" value={String(doses.length)} sub={`${summary.redoseCount} redose(s)`} severity={summary.redoseCount ? "relevante" : "info"} />
        <Metric icon={<Zap className="h-4 w-4" />} label="Interações" value={String(summary.interactions.length)} sub="fase + contexto" severity={summary.interactions.some((i) => ["urgente", "relevante"].includes(i.relevance)) ? "relevante" : "info"} />
        <Metric icon={<ClipboardList className="h-4 w-4" />} label="Adesão estimada" value={pct(summary.adherenceEstimate)} sub="por logs no período" severity={(summary.adherenceEstimate ?? 100) < 70 ? "cautela" : "info"} />
      </div>

      <Card className="min-w-0 space-y-3 border-primary/20 bg-primary/5 p-4">
        <div className="flex min-w-0 flex-col items-start gap-3 sm:flex-row sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-semibold"><BrainCircuit className="h-4 w-4 shrink-0 text-primary" /> Central de decisão clínica</div>
            <div className="break-words text-xs text-muted-foreground">O sistema cruza dose real, formulação, curva, sono, cafeína, check-in, uso de substâncias e histórico. Curva relativa, não sérica.</div>
          </div>
          <Badge variant="secondary" className="shrink-0">{periodDays} dias</Badge>
        </div>
        {summary.actionCards.length === 0 ? (
          <div className="text-sm text-muted-foreground">Ainda faltam dados para gerar ações clínicas robustas. Registre dose + formulação + check-in pós-dose.</div>
        ) : (
          <div className="grid min-w-0 gap-3 md:grid-cols-2">
            {summary.actionCards.slice(0, 8).map((a) => (
              <div key={a.id} className={cn("min-w-0 space-y-2 rounded-xl border p-3", toneClass(a.severity))}>
                <div className="flex min-w-0 items-start justify-between gap-2">
                  <div className="min-w-0 break-words text-sm font-medium">{a.title}</div>
                  <Badge variant="outline" className="shrink-0 bg-background/70 text-[10px]">{a.severity}</Badge>
                </div>
                <div className="break-words text-xs opacity-90">{a.rationale}</div>
                <div className="break-words text-xs"><span className="font-medium">Ação:</span> {a.action}</div>
                {a.missing?.length ? <div className="break-words text-[11px] opacity-80"><span className="font-medium">Falta:</span> {a.missing.join("; ")}</div> : null}
                <div className="text-[10px] uppercase tracking-wide opacity-70">confiança {a.confidence}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="min-w-0 space-y-3 p-4">
        <div className="flex items-center gap-2 font-medium"><ClipboardList className="h-4 w-4 shrink-0 text-primary" /> Sintomas-alvo conectados ao tratamento</div>
        {summary.targetSymptomSignals.length === 0 ? (
          <div className="text-sm text-muted-foreground">Sem sintomas-alvo definidos ou sem medidas longitudinais. Defina alvos com baseline/meta e registre intensidade semanal.</div>
        ) : (
          <div className="grid min-w-0 gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {summary.targetSymptomSignals.map((s) => (
              <div key={s.id} className="min-w-0 space-y-1 rounded-lg border p-3 text-xs">
                <div className="flex min-w-0 items-center justify-between gap-2">
                  <span className="min-w-0 break-words text-sm font-medium">{s.name}</span>
                  <Badge variant={s.direction === "piorando" ? "destructive" : s.direction === "melhorando" ? "default" : "secondary"} className="shrink-0 text-[10px]">{s.direction}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-1 text-muted-foreground">
                  <span>Baseline: {s.baseline ?? "—"}</span><span>Meta: {s.goal ?? "—"}</span>
                  <span>Atual: {s.current?.toFixed(1) ?? "—"}</span><span>Média 7d: {s.recentAverage?.toFixed(1) ?? "—"}</span>
                  <span>Δ baseline: {s.deltaVsBaseline?.toFixed(1) ?? "—"}</span><span>Δ recente: {s.deltaRecent?.toFixed(1) ?? "—"}</span>
                </div>
                {s.likelyDrivers.length ? <div className="break-words"><span className="font-medium">Moduladores:</span> {s.likelyDrivers.join(", ")}</div> : null}
                <div className="break-words text-[11px] opacity-80">Próximo dado: {s.nextData}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="min-w-0 space-y-3 p-4">
        <div className="flex min-w-0 flex-col items-start gap-2 sm:flex-row sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-medium"><LineChart className="h-4 w-4 shrink-0 text-primary" /> Curva viva e cargas por substância</div>
            <div className="break-words text-xs text-muted-foreground">Desmarque substâncias, expanda o gráfico e use tooltip/brush para ler horário, fase e intensidade 0–100.</div>
          </div>
          <Badge variant="outline" className="shrink-0">{summary.substanceLoads.length} substância(s)</Badge>
        </div>
        <div className="min-w-0 overflow-x-hidden">
          <AdvancedCurveExplorer doses={doses as any} title="Curva viva PK/PD + contexto" description="100 = maior intensidade relativa do pico daquela substância. A linha total é carga combinada saturada em 100 para evitar falsa precisão." />
        </div>
        <div className="grid min-w-0 gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {summary.substanceLoads.map((s) => (
            <div key={s.key} className="min-w-0 space-y-1 rounded-lg border p-3 text-xs">
              <div className="flex min-w-0 flex-col items-start gap-1 sm:flex-row sm:items-center sm:justify-between"><span className="min-w-0 break-words font-medium">{s.label}</span><Badge variant="secondary" className="shrink-0">{s.currentIntensity}/100 · {s.phase}</Badge></div>
              <div className="grid grid-cols-2 gap-1 text-muted-foreground">
                <span>Doses: {s.doseCount}</span><span>Redoses: {s.redoseCount}</span>
                <span>Benefício: {num(s.meanBenefit)}</span><span>Adverso: {num(s.meanAdverse)}</span>
                <span>Foco: {num(s.meanFocus)}</span><span>Ansiedade: {num(s.meanAnxiety)}</span>
                <span>Cafeína: {s.caffeineMg || 0} mg</span><span>Privação máx: {num(s.maxSleepDeprivation)}</span>
              </div>
              {s.missing.length ? <div className="break-words text-warning">Falta: {s.missing.join(", ")}</div> : <div className="text-success">Dados mínimos presentes.</div>}
            </div>
          ))}
        </div>
      </Card>

      {patientId ? (
        <div className="min-w-0"><DoseLogManager doses={doses as any[]} patientId={patientId} sessionId={sessionId ?? null} onChanged={onDoseChanged} /></div>
      ) : (
        <Card className="p-4 text-sm text-muted-foreground">Sem paciente para habilitar edição de doses.</Card>
      )}

      <Card className="min-w-0 space-y-3 p-4">
        <div className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4 shrink-0 text-warning" /> Interações e sobreposições priorizadas</div>
        {summary.interactions.length === 0 ? <div className="text-sm text-muted-foreground">Sem interação contextual detectada. Isso pode significar ausência de dados, não ausência real de risco.</div> : (
          <div className="grid min-w-0 gap-3 md:grid-cols-2">
            {summary.interactions.map((it, idx) => (
              <div key={idx} className={cn("min-w-0 space-y-1 rounded-lg border p-3", toneClass(it.relevance as any))}>
                <div className="flex min-w-0 flex-col items-start gap-2 sm:flex-row sm:items-center"><Badge variant="outline" className="shrink-0 bg-background/70">{it.relevance}</Badge><span className="min-w-0 break-words text-sm font-medium">{it.summary}</span></div>
                {it.detail && <div className="break-words text-xs opacity-90">{it.detail}</div>}
                {it.mechanism && <div className="break-words text-xs"><span className="font-medium">Mecanismo:</span> {it.mechanism}</div>}
                {it.monitor?.length ? <div className="break-words text-xs"><span className="font-medium">Monitorar:</span> {it.monitor.join(", ")}</div> : null}
                {it.action && <div className="break-words text-xs"><span className="font-medium">Ação:</span> {it.action}</div>}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="min-w-0 space-y-2 p-4">
        <div className="flex items-center gap-2 font-medium"><Database className="h-4 w-4 shrink-0 text-primary" /> Qualidade do dado: o que ainda impede inteligência real</div>
        {summary.missingCore.length === 0 ? <div className="text-sm text-success">Dados mínimos adequados para análise longitudinal inicial.</div> : (
          <ul className="space-y-1 break-words pl-5 text-sm list-disc">
            {summary.missingCore.map((m) => <li key={m}>{m}</li>)}
          </ul>
        )}
        <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => navigator.clipboard?.writeText(JSON.stringify(summary, null, 2))}>Copiar snapshot JSON da inteligência</Button>
      </Card>
    </div>
  );
}

function Metric({ icon, label, value, sub, severity }: any) {
  return (
    <Card className={cn("min-w-0 p-4", toneClass(severity))}>
      <div className="flex items-center justify-between gap-2 text-xs uppercase tracking-wide opacity-80"><span className="break-words">{label}</span>{icon}</div>
      <div className="mt-2 text-2xl font-semibold tabular-nums">{value}</div>
      <div className="break-words text-[11px] opacity-80">{sub}</div>
    </Card>
  );
}
